import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Checkbox,
  Grid,
  CircularProgress,
  Paper,
  Divider,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Google, Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { login, setTwoFaUsername, User } from "../../store/slice/AuthSlice";
import backgroundImage from "../../assets/backgroundImage.jpg";
import { motion } from "framer-motion";
import apiService from "../../service/Api";
import { useGoogleLogin } from "@react-oauth/google";
import { clearTheme, setTheme } from "../../store/slice/ThemeSlice";
import { IThemeState } from "../../types/theme";
import { RootState } from "../../store/Index";
import { showErrorToast } from "../../components/common/ShowToast";
import { easeInOut } from "framer-motion";

const Login = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const accessToken = useSelector(
    (state: RootState) => state.auth.access_token
  );

  const muzukuru = "../../assets/muzukuru.png";
  const muzukuruLogoToUse = reduxTheme.logo || muzukuru;
  const redirect =
    new URLSearchParams(location.search).get("redirect") || "/dashboard";

  useEffect(() => {
    if (accessToken) {
      navigate(redirect, { replace: true });
    }
  }, [accessToken, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    if (code) {
      handleGoogleAuthCode(code);
    }
  }, [location]);

  const handleGoogleAuthCode = async (code: string) => {
    setIsLoading(true);
    try {
      const response = await apiService.post<{
        access?: string;
        refresh?: string;
        language?: string;
        is_admin?: boolean;
        is_2fa_enabled: boolean;
        user: User;
        message?: string;
      }>("/api/auth/google/", { code }, true);

      if (response.data.is_2fa_enabled) {
        dispatch(setTwoFaUsername(response.data.user.username));
        navigate("/verify-2fa");
        return;
      }

      const { access, refresh, is_admin } = response.data;
      dispatch(
        login({
          user: {
            username: response.data.user.username,
            first_name: response.data.user.first_name,
            last_name: response.data.user.last_name,
            is_supper_user: response.data.user.is_supper_user,
            permissions: is_admin ? ["admin"] : [],
          },
          access_token: access!,
          refresh_token: refresh!,
          is_2fa_enabled: false,
        })
      );
      localStorage.setItem("access_token", access!);
      localStorage.setItem("refresh_token", refresh!);
      navigate("/setup-2fa");
    } catch (err: any) {
      console.error("Google auth error:", err);
      showErrorToast(
        err.response?.data?.error ||
          "Google authentication failed. Please try again.",
        "google-error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      if (codeResponse.code) {
        handleGoogleAuthCode(codeResponse.code);
      }
    },
    onError: (error) => {
      console.error("Google login error:", error);
      showErrorToast(
        "Google authentication failed. Please try again.",
        "google-error"
      );
    },
    flow: "auth-code",
    redirect_uri: window.location.origin + "/#/accounts/login",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      showErrorToast("Please enter both username and password.", "login-error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.post<{
        access_token?: string;
        refresh_token?: string;
        user: User;
        is_2fa_enabled: boolean;
        message?: string;
      }>("/api/authentication/login/", { username, password }, true);

      if (response.data.is_2fa_enabled) {
        dispatch(setTwoFaUsername(response.data.user.username));
        navigate("/verify-2fa");
        return;
      }

      dispatch(
        login({
          user: response.data.user,
          access_token: response.data.access_token!,
          refresh_token: response.data.refresh_token!,
          is_2fa_enabled: response.data.is_2fa_enabled,
        })
      );

      localStorage.setItem("access_token", response.data.access_token!);
      localStorage.setItem("refresh_token", response.data.refresh_token!);

      dispatch(clearTheme());
      try {
        const themeResponse = await apiService.get<IThemeState>(
          `/api/companies/theme/${encodeURIComponent(username)}`
        );
        dispatch(setTheme(themeResponse.data));
      } catch (themeErr) {
        console.error("Failed to fetch theme:", themeErr);
      }

      navigate("/setup-2fa");
    } catch (err: any) {
      console.error("Login error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      let errorMessage = "An error occurred. Please try again.";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      showErrorToast(errorMessage, "login-error");
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const formFields = [
    {
      label: "Username",
      value: username,
      setValue: setUsername,
      type: "text",
      placeholder: "Enter your username",
      fieldKey: "username",
    },
    {
      label: "Password",
      value: password,
      setValue: setPassword,
      type: showPassword ? "text" : "password",
      placeholder: "Enter your password",
      toggle: () => setShowPassword(!showPassword),
      show: showPassword,
      fieldKey: "password",
    },
  ];

  return (
    <Grid container sx={{ minHeight: "100vh", overflow: "hidden" }}>
      {/* Left Column - Enhanced Image Background */}
      <Grid
        size={{ xs: false, md: 6 }}
        sx={{
          position: "relative",
          display: { xs: "none", md: "block" },
          background: `linear-gradient(135deg, ${
            reduxTheme.primary_colors?.[500] || "#614438"
          }80 0%, ${
            reduxTheme.primary_colors?.[400] || "#C29363"
          }99 100%), url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(45deg, rgba(97, 68, 56, 0.1) 0%, transparent 50%)",
            zIndex: 1,
          },
        }}
      >
        {/* Floating geometric shapes */}
        <Box
          sx={{
            position: "absolute",
            top: "15%",
            right: "10%",
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            background: "rgba(194, 147, 99, 0.2)",
            zIndex: 2,
          }}
          component={motion.div}
          animate={{
            y: [0, -15, 0],
            rotate: [0, 90, 180],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: easeInOut,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "70%",
            right: "20%",
            width: "50px",
            height: "50px",
            borderRadius: "8px",
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            zIndex: 2,
          }}
          component={motion.div}
          animate={{
            x: [0, 20, 0],
            rotate: [0, -90, -180],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: easeInOut,
          }}
        />
        {/* Enhanced Logo Section */}
        <Box
          sx={{
            position: "absolute",
            bottom: 40,
            left: 40,
            display: "flex",
            alignItems: "center",
            zIndex: 5,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "16px",
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <motion.img
                alt="Muzukuru logo"
                width="165px"
                height="70px"
                src={muzukuruLogoToUse}
                style={{ cursor: "pointer" }}
              />
            </Paper>
          </motion.div>
        </Box>
        {/* Welcome text overlay */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            zIndex: 3,
            color: "white",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                background:
                  "linear-gradient(45deg, #fff 30%, rgba(194, 147, 99, 1) 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Welcome Back
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                fontWeight: 300,
              }}
            >
              Sign in to continue your journey
            </Typography>
          </motion.div>
        </Box>
      </Grid>
      {/* Right Column - Enhanced Login Form */}
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 4, md: 8 },
          py: { xs: 4, md: 0 },
          background: `linear-gradient(135deg, ${
            reduxTheme.neutral_colors?.[900] || "#fafafa"
          } 0%, ${reduxTheme.neutral_colors?.[800] || "#f5f5f5"} 100%)`,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 20% 80%, rgba(194, 147, 99, 0.05) 0%, transparent 50%)",
            zIndex: 1,
          },
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ width: "100%", maxWidth: "480px", zIndex: 2 }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(20px)",
              padding: { xs: "24px", sm: "32px", md: "40px" },
              borderRadius: "24px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: `linear-gradient(90deg, ${
                  reduxTheme.primary_colors?.[400] || "#614438"
                } 0%, ${reduxTheme.primary_colors?.[300] || "#C29363"} 100%)`,
              },
            }}
            component={motion.div}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <Box component="form" onSubmit={handleSubmit}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${
                      reduxTheme.primary_colors?.[400] || "#614438"
                    } 0%, ${
                      reduxTheme.primary_colors?.[300] || "#C29363"
                    } 100%)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    textAlign: "center",
                    marginBottom: "8px",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Sign In
                </Typography>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="body1"
                  sx={{
                    color: reduxTheme.neutral_colors?.[500] || "#666",
                    textAlign: "center",
                    marginBottom: "24px",
                    fontSize: "16px",
                    lineHeight: 1.6,
                  }}
                >
                  Sign in to access your account and continue.
                </Typography>
              </motion.div>
              {formFields.map((field, index) => (
                <motion.div key={index} variants={itemVariants}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        color: reduxTheme.neutral_colors?.[100] || "#333",
                        fontSize: "14px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {field.label}
                    </Typography>
                    <TextField
                      placeholder={field.placeholder}
                      variant="outlined"
                      fullWidth
                      type={field.type}
                      value={field.value}
                      onChange={(e) => field.setValue(e.target.value)}
                      onFocus={() => setFocusedField(field.fieldKey)}
                      onBlur={() => setFocusedField(null)}
                      disabled={isLoading}
                      InputProps={{
                        ...(field.toggle && {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={field.toggle}
                                edge="end"
                                disabled={isLoading}
                                sx={{
                                  color: field.show
                                    ? reduxTheme.primary_colors?.[400] ||
                                      "#614438"
                                    : reduxTheme.neutral_colors?.[400] ||
                                      "#999",
                                  "&:hover": {
                                    backgroundColor: "rgba(97, 68, 56, 0.04)",
                                  },
                                }}
                              >
                                {field.show ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          transition: "all 0.3s ease",
                          height: "56px",
                          "& fieldset": {
                            borderColor:
                              reduxTheme.neutral_colors?.[300] ||
                              "rgba(0,0,0,0.1)",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor:
                              reduxTheme.neutral_colors?.[400] ||
                              "rgba(194, 147, 99, 0.5)",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                            transform: "translateY(-2px)",
                            boxShadow: `0 8px 25px rgba(${
                              reduxTheme.primary_colors?.[400]
                                ? parseInt(
                                    reduxTheme.primary_colors[400].slice(1, 3),
                                    16
                                  )
                                : 97
                            }, ${
                              reduxTheme.primary_colors?.[400]
                                ? parseInt(
                                    reduxTheme.primary_colors[400].slice(3, 5),
                                    16
                                  )
                                : 68
                            }, ${
                              reduxTheme.primary_colors?.[400]
                                ? parseInt(
                                    reduxTheme.primary_colors[400].slice(5, 7),
                                    16
                                  )
                                : 56
                            }, 0.15)`,
                            "& fieldset": {
                              borderColor:
                                reduxTheme.primary_colors?.[400] || "#614438",
                            },
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: reduxTheme.neutral_colors?.[100] || "#333",
                          fontSize: "16px",
                        },
                      }}
                    />
                  </Box>
                </motion.div>
              ))}
              <motion.div variants={itemVariants}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        sx={{
                          color: reduxTheme.primary_colors?.[400] || "#614438",
                          "&.Mui-checked": {
                            color:
                              reduxTheme.primary_colors?.[400] || "#614438",
                          },
                        }}
                        disabled={isLoading}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: reduxTheme.neutral_colors?.[100] || "#333",
                        }}
                      >
                        Remember me
                      </Typography>
                    }
                  />
                  <Link
                    to="/forgot-password"
                    style={{
                      color: reduxTheme.primary_colors?.[300] || "#C29363",
                      textDecoration: "none",
                      fontSize: 14,
                    }}
                  >
                    Forgot Password?
                  </Link>
                </Box>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  component={motion.button}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    background: `linear-gradient(135deg, ${
                      reduxTheme.primary_colors?.[400] || "#614438"
                    } 0%, ${
                      reduxTheme.primary_colors?.[300] || "#C29363"
                    } 100%)`,
                    textTransform: "none",
                    padding: "16px",
                    roulette: "12px",
                    mb: 3,
                    fontSize: "16px",
                    fontWeight: 600,
                    boxShadow: `0 8px 25px rgba(${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(1, 3),
                            16
                          )
                        : 97
                    }, ${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(3, 5),
                            16
                          )
                        : 68
                    }, ${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(5, 7),
                            16
                          )
                        : 56
                    }, 0.3)`,
                    border: "none",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${
                        reduxTheme.primary_colors?.[500] || "#503629"
                      } 0%, ${
                        reduxTheme.primary_colors?.[400] || "#614438"
                      } 100%)`,
                      boxShadow: `0 12px 35px rgba(${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(1, 3),
                              16
                            )
                          : 97
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(3, 5),
                              16
                            )
                          : 68
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(5, 7),
                              16
                            )
                          : 56
                      }, 0.4)`,
                    },
                    "&:disabled": {
                      background: `rgba(${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(1, 3),
                              16
                            )
                          : 97
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(3, 5),
                              16
                            )
                          : 68
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(5, 7),
                              16
                            )
                          : 56
                      }, 0.3)`,
                    },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isLoading ? (
                    <CircularProgress
                      size={24}
                      sx={{ color: reduxTheme.neutral_colors?.[100] || "#fff" }}
                    />
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Divider
                    sx={{
                      flex: 1,
                      borderColor:
                        reduxTheme.neutral_colors?.[300] || "rgba(0,0,0,0.1)",
                    }}
                  />
                  <Typography
                    sx={{
                      px: 2,
                      color: reduxTheme.neutral_colors?.[500] || "#999",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    or
                  </Typography>
                  <Divider
                    sx={{
                      flex: 1,
                      borderColor:
                        reduxTheme.neutral_colors?.[300] || "rgba(0,0,0,0.1)",
                    }}
                  />
                </Box>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => googleLogin()}
                  disabled={isLoading}
                  component={motion.button}
                  whileHover={{ scale: 1.02 }}
                  sx={{
                    textTransform: "none",
                    padding: "16px",
                    borderRadius: "12px",
                    mb: 3,
                    borderColor:
                      reduxTheme.neutral_colors?.[300] || "rgba(0,0,0,0.1)",
                    borderWidth: "2px",
                    color: reduxTheme.neutral_colors?.[100] || "#666",
                    fontSize: "16px",
                    fontWeight: 600,
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    "&:hover": {
                      borderColor:
                        reduxTheme.neutral_colors?.[400] ||
                        "rgba(194, 147, 99, 0.3)",
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                    },
                    "&:disabled": {
                      borderColor:
                        reduxTheme.neutral_colors?.[400] || "rgba(0,0,0,0.1)",
                      color: reduxTheme.neutral_colors?.[500] || "#999",
                    },
                  }}
                  startIcon={<Google sx={{ color: "#DB4437" }} />}
                >
                  Sign in with Google
                </Button>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    color: reduxTheme.neutral_colors?.[100] || "#666",
                    fontSize: "15px",
                  }}
                >
                  Don't have an account?{" "}
                  <Link
                    to="/signup"
                    style={{
                      textDecoration: "none",
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${
                        reduxTheme.primary_colors?.[300] || "#C29363"
                      } 0%, ${
                        reduxTheme.primary_colors?.[400] || "#614438"
                      } 100%)`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    <motion.span whileHover={{ scale: 1.05 }}>
                      Sign Up
                    </motion.span>
                  </Link>
                </Typography>
              </motion.div>
            </Box>
          </Paper>
        </motion.div>
      </Grid>
    </Grid>
  );
};

export default Login;
